import { trackEvent } from 'actions/analytics';
import { sleep } from 'utils/sleep/index';
import { setError } from 'actions/error';
import moment from 'moment';
import {
  CreatePendingBookingAPIRequest,
  CreateReservationAPIRequest,
  DeleteBookingAPIRequest,
  DeleteReservationAPIRequest,
  EditBookingAPIRequest,
  EditLiveBookingAPIRequest,
  FetchBookingByReservationShortCodeAPIRequest,
  FetchCompletedBookingsAPIRequest,
  FetchIQOSBookingsAPIRequest,
  FetchReservationsAPIRequest,
  GetBookingsAPIRequest,
  GetPendingBookingsAPIRequest,
  RefundBookingAPIRequest,
  SearchBookingsAPIRequest,
  SendBookingReminderAPIRequest,
  UpdateReservationAPIRequest,
} from 'utils/constants/network';
import { callAPI } from 'utils/network';

export const REQUEST_UPDATE_LIVE_BOOKING = 'REQUEST_UPDATE_LIVE_BOOKING';
export const LIVE_BOOKING_UPDATED = 'LIVE_BOOKING_UPDATED';
export const REQUEST_LIVE_BOOKINGS = 'REQUEST_LIVE_BOOKINGS';
export const REQUEST_BOOKINGS = 'REQUEST_BOOKINGS';
export const SET_LIVE_BOOKINGS = 'SET_LIVE_BOOKINGS';
export const SET_BOOKINGS = 'SET_BOOKINGS';
export const SET_RESERVATIONS = 'SET_RESERVATIONS';
export const SET_BOOKING_RESERVATIONS = 'SET_BOOKING_RESERVATIONS';
export const SET_SELECTED_DATE = 'SET_SELECTED_DATE';
export const REQUEST_CREATE_BOOKING = 'REQUEST_CREATE_BOOKING';
export const REQUEST_CREATE_RESERVATION = 'REQUEST_CREATE_RESERVATION';
export const CREATED_PENDING_BOOKING = 'CREATED_PENDING_BOOKING';
export const CREATE_RESERVATION = 'CREATE_RESERVATION';
export const EDITED_BOOKING = 'EDITED_BOOKING';
export const DELETED_RESERVATION = 'DELETED_RESERVATION';
export const UPDATED_RESERVATION = 'UPDATED_RESERVATION';
export const REQUEST_EDIT_BOOKING = 'REQUEST_EDIT_BOOKING';
export const DELETED_BOOKING = 'DELETED_BOOKING';
export const REQUEST_DELETE_BOOKING = 'REQUEST_DELETE_BOOKING';
export const REFUNDED_BOOKING = 'REFUNDED_BOOKING';
export const REQUEST_REFUND_BOOKING = 'REQUEST_REFUND_BOOKING';
export const REQUEST_PENDING_BOOKINGS = 'REQUEST_PENDING_BOOKINGS';
export const SET_PENDING_BOOKINGS = 'SET_PENDING_BOOKINGS';
export const SEND_BOOKING_REMINDER = 'SEND_BOOKING_REMINDER';
export const SENT_BOOKING_REMINDER = 'SENT_BOOKING_REMINDER';
export const REQUEST_SEARCH_BOOKINGS = 'REQUEST_SEARCH_BOOKINGS';
export const SET_SEARCH_BOOKINGS = 'SET_SEARCH_BOOKINGS';
export const REQUEST_COMPLETED_BOOKINGS = 'REQUEST_COMPLETED_BOOKINGS';
export const SET_COMPLETED_BOOKINGS = 'SET_COMPLETED_BOOKINGS';
export const REQUEST_IQOS_BOOKINGS = 'REQUEST_IQOS_BOOKINGS';
export const SET_IQOS_BOOKINGS = 'SET_IQOS_BOOKINGS';
export const QUEUE_DRAFT_BOOKING = 'QUEUE_DRAFT_BOOKING';
export const REMOVE_DRAFT_BOOKING = 'REMOVE_DRAFT_BOOKING';
export const PROCESS_QUEUED_BOOKINGS = 'PROCESS_QUEUED_BOOKINGS';
export const CLEAR_BOOKING_FLAGS = 'CLEAR_BOOKING_FLAGS';
export const UPDATE_LOCAL_LIVE_BOOKINGS = 'UPDATE_LOCAL_LIVE_BOOKINGS';

export const clearBookingFlags = () => {
  return {
    type: CLEAR_BOOKING_FLAGS,
    payload: {
      fetchingBookings: false,
      fetchingIQOSBookings: false,
      fetchingCompletedBookings: false,
      fetchingPendingBookings: false,
      fetchingSearchBookings: false,
      fetchingLiveBookings: false,
      updatingLiveBooking: false,
      creatingBooking: false,
      creatingReservation: false,
      editingBooking: false,
      refundingBooking: false,
      sendingReminder: false,
      deletingBooking: false,
    },
  };
};

const requestBookings = () => ({
  type: REQUEST_BOOKINGS,
  payload: {
    fetchingBookings: true,
  },
});

export const updateLocalLiveBookings = newBookingsList => {
  return {
    type: UPDATE_LOCAL_LIVE_BOOKINGS,
    payload: {
      liveBookings: newBookingsList,
    },
  };
};

const requestLiveBookings = () => ({
  type: REQUEST_LIVE_BOOKINGS,
  payload: {
    fetchingLiveBookings: true,
  },
});

const requestUpdateLiveBooking = bookingId => ({
  type: REQUEST_UPDATE_LIVE_BOOKING,
  payload: {
    bookingId,
    updatingLiveBooking: true,
  },
});

const liveBookingUpdated = bookingId => ({
  type: LIVE_BOOKING_UPDATED,
  payload: {
    bookingId,
    updatingLiveBooking: false,
  },
});

const requestIQOSBookings = () => ({
  type: REQUEST_IQOS_BOOKINGS,
  payload: {
    fetchingIQOSBookings: true,
    IQOSBookingsFetched: false,
  },
});

const requestCompletedBookings = () => ({
  type: REQUEST_COMPLETED_BOOKINGS,
  payload: {
    fetchingCompletedBookings: true,
    completedBookingsFetched: false,
  },
});

const requestPendingBookings = () => ({
  type: REQUEST_PENDING_BOOKINGS,
  payload: {
    fetchingPendingBookings: true,
  },
});

const requestSearchBookings = () => ({
  type: REQUEST_SEARCH_BOOKINGS,
  payload: {
    fetchingSearchBookings: true,
  },
});

export const setSearchBookings = (bookings, success = true) => ({
  type: SET_SEARCH_BOOKINGS,
  payload: {
    searchBookingList: bookings,
    fetchingSearchBookings: !success,
  },
});

const requestCreateBooking = () => ({
  type: REQUEST_CREATE_BOOKING,
  payload: {
    creatingBooking: true,
    bookingCreated: false,
    creatingBookingErr: null,
  },
});

const requestCreateReservation = () => ({
  type: REQUEST_CREATE_RESERVATION,
  payload: {
    creatingReservation: true,
    bookingCreated: false,
    creatingBookingErr: null,
  },
});

const createdPendingBooking = (created, err) => ({
  type: CREATED_PENDING_BOOKING,
  payload: {
    creatingBooking: false,
    bookingCreated: created,
    creatingBookingErr: err,
  },
});

const requestEditBooking = () => ({
  type: REQUEST_EDIT_BOOKING,
  payload: {
    editingBooking: true,
    bookingEdited: false,
  },
});

const editedBooking = edited => ({
  type: EDITED_BOOKING,
  payload: {
    editingBooking: false,
    bookingEdited: edited,
  },
});

const requestSendBookingReminder = () => ({
  type: SEND_BOOKING_REMINDER,
  payload: {
    sendingReminder: true,
    reminderSent: false,
  },
});

const sentBookingReminder = success => ({
  type: SENT_BOOKING_REMINDER,
  payload: {
    sendingReminder: false,
    reminderSent: success,
  },
});

const requestRefundBooking = () => ({
  type: REQUEST_REFUND_BOOKING,
  payload: {
    refundingBooking: true,
    bookingRefunded: false,
  },
});

const refundedBooking = refunded => ({
  type: REFUNDED_BOOKING,
  payload: {
    refundingBooking: false,
    bookingRefunded: refunded,
  },
});

const requestDeleteBooking = () => ({
  type: REQUEST_DELETE_BOOKING,
  payload: {
    deletingBooking: true,
    bookingDeleted: false,
  },
});

const deletedBooking = deleted => ({
  type: DELETED_BOOKING,
  payload: {
    deletingBooking: false,
    bookingDeleted: deleted,
  },
});

const deletedReservation = deleted => ({
  type: DELETED_RESERVATION,
  payload: {
    deletingReservation: false,
    bookingDeleted: deleted,
  },
});

const updatedReservation = updated => ({
  type: UPDATED_RESERVATION,
  payload: {
    updatingReservation: false,
    reservationUpdated: updated,
  },
});

export const setLiveBookings = bookings => ({
  type: SET_LIVE_BOOKINGS,
  payload: {
    liveBookings: bookings,
    fetchingLiveBookings: false,
  },
});

export const setBookings = bookings => ({
  type: SET_BOOKINGS,
  payload: {
    bookingList: bookings,
    fetchingBookings: false,
  },
});

export const setReservations = reservations => ({
  type: SET_RESERVATIONS,
  payload: {
    reservationsList: reservations,
    fetchingReservations: false,
  },
});

export const setBookingReservations = reservations => ({
  type: SET_BOOKING_RESERVATIONS,
  payload: {
    bookingReservationsList: reservations,
    fetchingBookingReservations: false,
  },
});

export const setIQOSBookings = (bookings, success) => ({
  type: SET_IQOS_BOOKINGS,
  payload: {
    fetchingIQOSBookings: false,
    IQOSBookingsFetched: success,
    IQOSBookings: bookings,
  },
});

export const setCompletedBookings = (bookings, success) => ({
  type: SET_COMPLETED_BOOKINGS,
  payload: {
    fetchingCompletedBookings: false,
    completedBookingsFetched: success,
    completedBookings: bookings,
  },
});

export const setPendingBookings = bookings => ({
  type: SET_PENDING_BOOKINGS,
  payload: {
    pendingBookingList: bookings,
    fetchingPendingBookings: false,
  },
});

export const setSelectedDate = date => {
  return {
    type: SET_SELECTED_DATE,
    payload: {
      selectedDate: date,
    },
  };
};

export const queueBooking = booking => ({
  type: QUEUE_DRAFT_BOOKING,
  payload: {
    bookingToQueue: booking,
  },
});

export const removeQueuedBooking = booking => ({
  type: REMOVE_DRAFT_BOOKING,
  payload: {
    bookingToDequeue: booking,
  },
});

export const processQueuedBookings = () => ({
  type: PROCESS_QUEUED_BOOKINGS,
  payload: {},
});

/**
 * Dispatches actions to fetch bookings and eventually sets the booking list from the server response.
 * @function fetchBookings
 * @memberof BookingActions
 * @param {moment.Moment} from - The moment date which you wish to fetch bookings from
 * @param {moment.Moment} to - The moment date which you wish to fetch bookings until
 * @return {Promise<object>}
 */
export const fetchBookings = (from, to) => {
  return dispatch => {
    dispatch(requestBookings());

    from = moment(from);
    to = moment(to);

    const apiRequest = GetBookingsAPIRequest({
      from: from.toISOString(false),
      to: to.toISOString(false),
    });

    return callAPI(apiRequest)
      .then(json => dispatch(setBookings(json.bookings)))
      .catch(err => {
        dispatch(setError(err));
        dispatch(setBookings(null));
      });
  };
};

export const fetchReservations = venue_id => {
  return dispatch => {
    const apiRequest = FetchReservationsAPIRequest({
      venue_id: venue_id,
    });

    return callAPI(apiRequest)
      .then(json => {
        const sortedReservations = json.reservations.sort((a, b) => {
          return new Date(a.reserved_from) - new Date(b.reserved_from);
        });
        dispatch(setReservations(sortedReservations));
      })
      .catch(err => {
        dispatch(setError(err));
        dispatch(setBookings(null));
      });
  };
};

export const fetchBookingByReservationShortCode = reservation_shortcode => {
  return dispatch => {
    const apiRequest = FetchBookingByReservationShortCodeAPIRequest({
      reservation_shortcode: reservation_shortcode,
    });
    return callAPI(apiRequest)
      .then(json => dispatch(setBookingReservations(json)))
      .catch(err => {
        dispatch(setError(err));
        dispatch(setBookings(null));
      });
  };
};

export const moveLiveBooking = (booking, newStatus, prepAreaLabel) => {
  return async dispatch => {
    dispatch(requestUpdateLiveBooking(booking.id));

    const apiRequest = EditLiveBookingAPIRequest({
      booking: booking.id,
      newStatus,
      prepAreaLabel,
    });

    try {
      await callAPI(apiRequest);
      dispatch(liveBookingUpdated(booking.id));
    } catch (e) {
      dispatch(setError(e));
      dispatch(liveBookingUpdated(booking.id));
    }
  };
};

export const fetchLiveBookings = prepAreaLabel => {
  return async dispatch => {
    dispatch(requestLiveBookings());

    const params = {
      live: true,
      prepAreaLabel,
    };

    const apiRequest = GetBookingsAPIRequest(params);

    try {
      const json = await callAPI(apiRequest);
      dispatch(setLiveBookings(json.bookings));
    } catch (e) {
      dispatch(setError(e));
      dispatch(setLiveBookings(null));
    }
  };
};

export const fetchCompletedBookings = (searchQuery, page, from, to) => {
  return dispatch => {
    dispatch(requestCompletedBookings());

    const apiRequest = FetchCompletedBookingsAPIRequest({
      pagination: {
        page: page,
        limit: 20,
      },
      query: searchQuery,
      from: from || moment().subtract(1, 'weeks').startOf('day').toISOString(),
      to: to || moment().toISOString(),
    });

    return callAPI(apiRequest)
      .then(json => dispatch(setCompletedBookings(json.bookings || [], true)))
      .catch(err => dispatch(setCompletedBookings([], false)));
  };
};

export const fetchAllCompletedBookings =
  (searchQuery, from, to) => async () => {
    const CHUNK = 600;
    let allBookings = [];
    let page = 0;

    while (true) {
      const apiRequest = FetchCompletedBookingsAPIRequest({
        pagination: { page, limit: CHUNK },
        query: searchQuery,
        from:
          from || moment().subtract(1, 'weeks').startOf('day').toISOString(),
        to: to || moment().toISOString(),
      });

      const json = await callAPI(apiRequest);
      const chunk = (json && json.bookings) || [];
      allBookings = allBookings.concat(chunk);

      if (chunk.length < CHUNK) break;
      page += 1;
      await sleep(50);
    }

    return allBookings;
  };

export const fetchIQOSBookings = from => {
  return dispatch => {
    dispatch(requestIQOSBookings());

    from = moment(from);

    const apiRequest = FetchIQOSBookingsAPIRequest({
      from: from.toISOString(false),
      pagination: {
        page: 0,
        limit: 1000,
      },
      global: true,
    });

    return callAPI(apiRequest)
      .then(json => dispatch(setIQOSBookings(json.bookings, true)))
      .catch(err => dispatch(setIQOSBookings([], false)));
  };
};

export const searchBookings = query => {
  return dispatch => {
    dispatch(requestSearchBookings());

    const apiRequest = SearchBookingsAPIRequest({
      query: query,
    });

    return callAPI(apiRequest, false, true)
      .then(json => dispatch(setSearchBookings(json.bookings)))
      .catch(err => {
        if (err.name === 'AbortError') {
          return;
        }

        dispatch(setError(err));
        dispatch(setSearchBookings([], false));
      });
  };
};

export const fetchPendingBookings = from => {
  return dispatch => {
    dispatch(requestPendingBookings());

    from = moment(from);

    const apiRequest = GetPendingBookingsAPIRequest({
      from: from.toISOString(false),
      pagination: {
        page: 0,
        limit: 1000,
      },
    });

    return callAPI(apiRequest)
      .then(json => dispatch(setPendingBookings(json.bookings)))
      .catch(err => {
        dispatch(setError(err));
        dispatch(setPendingBookings([]));
      });
  };
};

export const createPendingBooking = reqData => {
  return dispatch => {
    dispatch(requestCreateBooking());

    const apiRequest = CreatePendingBookingAPIRequest(reqData);

    return callAPI(apiRequest, false, true)
      .then(json => {
        dispatch(
          trackEvent('booking link sent', json.booking.id, json.booking.email, {
            deposit: json.booking.force_deposit.amount,
            name: json.booking.name,
          })
        );

        dispatch(createdPendingBooking(true, null));
      })
      .catch(err => {
        if (err.message === 'timeout') {
          // the internet connection is messy, save the booking as a draft
          reqData.booking.draft = true;
          reqData.booking.draft_timestamp = new Date().getTime();

          dispatch(queueBooking(reqData.booking));
        }

        dispatch(createdPendingBooking(false, err.message));
      });
  };
};

export const createReservations = reqData => {
  return dispatch => {
    dispatch(requestCreateReservation());

    const apiRequest = CreateReservationAPIRequest(reqData);

    return callAPI(apiRequest, false, true)
      .then(json => {
        dispatch(
          trackEvent('booking link sent', json.booking.id, json.booking.email, {
            deposit: json.booking.force_deposit.amount,
            name: json.booking.name,
          })
        );

        dispatch(requestCreateReservation(true, null));
      })
      .catch(err => {
        if (err.message === 'timeout') {
          // the internet connection is messy, save the booking as a draft
          reqData.booking.draft = true;
          reqData.booking.draft_timestamp = new Date().getTime();

          dispatch(queueBooking(reqData.booking));
        }

        dispatch(requestCreateReservation(false, err.message));
      });
  };
};

export const sendBookingReminder = bookingID => {
  return dispatch => {
    dispatch(requestSendBookingReminder());

    const apiRequest = SendBookingReminderAPIRequest({ booking: bookingID });

    return callAPI(apiRequest)
      .then(json => dispatch(sentBookingReminder(true)))
      .catch(err => {
        dispatch(sentBookingReminder(false));
      });
  };
};

export const editBooking = booking => {
  return dispatch => {
    dispatch(requestEditBooking());

    const apiRequest = EditBookingAPIRequest(booking);

    return callAPI(apiRequest)
      .then(json => dispatch(editedBooking(true)))
      .catch(err => {
        dispatch(editedBooking(false));
      });
  };
};

export const refundBooking = (id, amountPence) => {
  return dispatch => {
    dispatch(requestRefundBooking());

    const data = { id: id };

    if (amountPence) {
      data['amount'] = amountPence;
    }

    const apiRequest = RefundBookingAPIRequest(data);

    return callAPI(apiRequest)
      .then(json => dispatch(refundedBooking(true)))
      .catch(err => {
        dispatch(refundedBooking(false));
      });
  };
};

export const deleteBooking = (id, applyRefund) => {
  return dispatch => {
    dispatch(requestDeleteBooking());

    const apiRequest = DeleteBookingAPIRequest({ id: id, refund: applyRefund });

    return callAPI(apiRequest)
      .then(json => dispatch(deletedBooking(true)))
      .catch(err => {
        dispatch(deletedBooking(false));
      });
  };
};

export const deleteReservation = id => {
  return dispatch => {
    dispatch(requestDeleteBooking());

    const apiRequest = DeleteReservationAPIRequest({ reservation_id: id });

    return callAPI(apiRequest)
      .then(json => dispatch(deletedReservation(true)))
      .catch(err => {
        dispatch(deletedReservation(false));
      });
  };
};

export const UpdateReservation = reservation => {
  return dispatch => {
    dispatch(requestDeleteBooking());

    const apiRequest = UpdateReservationAPIRequest(reservation);

    return callAPI(apiRequest)
      .then(json => dispatch(updatedReservation(true)))
      .catch(err => {
        dispatch(updatedReservation(false));
      });
  };
};



// WEBPACK FOOTER //
// ./src/actions/booking/index.js